import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ImportOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import MomentDatePicker from "../../MomentDatePicker";
import config from "../../../api_config.json";

const LIMIT_LABELS = {
  max_cloud_storage_bytes: "Max Spazio Cloud",
  max_communication_templates: "Max Modelli",
  max_custom_documents: "Max Documenti Personalizzati",
  max_obituaries: "Max Necrologi",
  max_smtp_external_communications: "Max Invii SMTP Anno",
};

const BYTES_IN_GB = 1024 ** 3;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

const formatLimitAmount = (key, amount) => {
  if (key === "max_cloud_storage_bytes") {
    return `${(Number(amount || 0) / BYTES_IN_GB).toLocaleString("it-IT", {
      maximumFractionDigits: 2,
    })} GB`;
  }

  return Number(amount || 0).toLocaleString("it-IT");
};

const normalizeLimitKey = (key) => String(key || "").trim();

const getLimitLabel = (key) => {
  const normalizedKey = normalizeLimitKey(key);
  return LIMIT_LABELS[normalizedKey] || normalizedKey;
};

const getActionTag = (action) => {
  if (action === "create") {
    return <Tag color="green">Da creare</Tag>;
  }

  if (action === "update") {
    return <Tag color="blue">Da aggiornare</Tag>;
  }

  if (action === "unchanged") {
    return <Tag>Già presente</Tag>;
  }

  return <Tag>{action || "-"}</Tag>;
};

const getQuantityLabel = (record) => {
  const quantity = record.selectedQuantity ?? record.includedQuantity;
  const suffix = record.unitOfMeasure ? ` ${record.unitOfMeasure}` : "";

  if (quantity === null || quantity === undefined) {
    return "-";
  }

  return `${quantity}${suffix}`;
};

const getDateIso = (dateLike) => dateLike?.iso || dateLike || null;

const ImportQuoteModal = ({ visible, onCancel, onSuccess, orgId, org }) => {
  const [quoteInput, setQuoteInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const baseUrl = config.baseUrl[env];

  useEffect(() => {
    if (!visible) {
      setQuoteInput("");
      setPreview(null);
      form.resetFields();
    }
  }, [form, visible]);

  const requestImport = async (payload) => {
    const token = await getTokenSilently();
    const response = await axios({
      method: "post",
      url: `${baseUrl}/orgs/${orgId}/modules/import-from-quote`,
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data?.error) {
      throw new Error(response.data.error);
    }

    return response.data?.data;
  };

  const handlePreview = async () => {
    if (!quoteInput.trim()) {
      message.warning("Inserisci un quote id o URL");
      return;
    }

    setPreviewLoading(true);
    try {
      const result = await requestImport({
        quoteInput,
        dryRun: true,
        updateOrg: true,
      });

      setPreview(result);
      const orgStartDate =
        getDateIso(org?.subscription_started_at) ||
        getDateIso(org?.stripe_created_at);
      const orgExpiryDate = getDateIso(org?.data_scadenza);
      form.setFieldsValue({
        startDate: orgStartDate
          ? moment(orgStartDate)
          : result.defaults?.startDate
            ? moment(result.defaults.startDate)
            : null,
        expiryDate: orgExpiryDate
          ? moment(orgExpiryDate)
          : result.defaults?.expiryDate
            ? moment(result.defaults.expiryDate)
            : null,
        updateOrg: true,
        replaceExisting: false,
      });
    } catch (error) {
      console.error("Error loading quote preview:", error);
      message.error(`Errore anteprima: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview) {
      message.warning("Carica prima l'anteprima del preventivo");
      return;
    }

    const values = await form.validateFields();
    setSubmitting(true);

    try {
      await requestImport({
        quoteInput,
        dryRun: false,
        startDate: values.startDate?.toISOString(),
        expiryDate: values.expiryDate?.toISOString(),
        updateOrg: values.updateOrg,
        replaceExisting: values.replaceExisting,
      });

      message.success("Import completato");
      onSuccess();
    } catch (error) {
      console.error("Error importing quote:", error);
      message.error(`Errore import: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const moduleColumns = [
    {
      title: "Modulo",
      key: "name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{record.name || record.moduleId}</Typography.Text>
          {record.category && (
            <Typography.Text type="secondary">
              {record.category}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      render: (sku) => (sku ? <Tag>{sku}</Tag> : "-"),
    },
    {
      title: "Tipo",
      key: "type",
      render: (_, record) => (
        <Space size={4} wrap>
          {record.isSelected && <Tag color="blue">Selezionato</Tag>}
          {record.isIncluded && <Tag color="green">Incluso</Tag>}
          {record.quantity && <Tag color="purple">Quantità</Tag>}
        </Space>
      ),
    },
    {
      title: "Quantità",
      key: "quantity",
      render: (_, record) => getQuantityLabel(record),
    },
    {
      title: "Azione",
      dataIndex: "action",
      key: "action",
      render: getActionTag,
    },
  ];

  const limits = useMemo(
    () =>
      Object.entries(preview?.limits || {}).map(([key, amount]) => ({
        key: normalizeLimitKey(key),
        label: getLimitLabel(key),
        amount,
      })),
    [preview],
  );

  return (
    <Modal
      title="Importa da preventivo"
      open={visible}
      onCancel={onCancel}
      width={1100}
      onOk={handleImport}
      okText="Importa"
      cancelText="Annulla"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !preview }}
    >
      <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
        <Input
          value={quoteInput}
          onChange={(event) => setQuoteInput(event.target.value)}
          onPressEnter={handlePreview}
          placeholder="Quote id o URL Eterno"
        />
        <Button
          icon={<SearchOutlined />}
          loading={previewLoading}
          onClick={handlePreview}
        >
          Anteprima
        </Button>
      </Space.Compact>

      {preview && (
        <>
          <Descriptions size="small" bordered column={3}>
            <Descriptions.Item label="Preventivo">
              {preview.quote.objectId}
            </Descriptions.Item>
            <Descriptions.Item label="Stato">
              <Tag>{preview.quote.status || "-"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Prodotto">
              {preview.quote.productLine?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Frequenza">
              {preview.quote.paymentFrequency === "yearly"
                ? "Annuale"
                : "Mensile"}
            </Descriptions.Item>
            <Descriptions.Item label="Servizi">
              {preview.quote.numberOfServices || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Totale">
              {formatCurrency(preview.quote.total)}
            </Descriptions.Item>
          </Descriptions>

          {preview.warnings?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message="Import con avvisi"
              description={preview.warnings
                .map((warning) => warning.message)
                .join(" ")}
            />
          )}

          <Divider orientation="left">Opzioni</Divider>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              updateOrg: true,
              replaceExisting: false,
            }}
          >
            <Space size="large" align="start" wrap>
              <Form.Item
                name="startDate"
                label="Data inizio"
                rules={[{ required: true, message: "Seleziona una data" }]}
              >
                <MomentDatePicker format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item
                name="expiryDate"
                label="Scadenza abbonamento"
                rules={[{ required: true, message: "Seleziona una scadenza" }]}
              >
                <MomentDatePicker format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item name="updateOrg" valuePropName="checked">
                <Checkbox>Aggiorna dati abbonamento org</Checkbox>
              </Form.Item>
              <Form.Item name="replaceExisting" valuePropName="checked">
                <Checkbox>
                  Disabilita moduli non presenti nel preventivo
                </Checkbox>
              </Form.Item>
            </Space>
          </Form>

          <Divider orientation="left">
            Moduli ({preview.modules.items.length})
          </Divider>
          <Table
            size="small"
            rowKey={(record) => record.moduleId}
            columns={moduleColumns}
            dataSource={preview.modules.items}
            pagination={false}
            scroll={{ y: 320 }}
          />

          {limits.length > 0 && (
            <>
              <Divider orientation="left">Limiti calcolati</Divider>
              <Space size={[8, 8]} wrap>
                {limits.map((limit) => (
                  <Tag key={limit.key} color="cyan">
                    {limit.label}: {formatLimitAmount(limit.key, limit.amount)}
                  </Tag>
                ))}
              </Space>
            </>
          )}

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            icon={<ImportOutlined />}
            message={`${preview.modules.create} moduli da creare, ${preview.modules.update} da aggiornare, ${preview.modules.unchanged || 0} già presenti`}
            description="I moduli manuali non vengono rimossi. Le quantita del preventivo sono applicate tramite i limiti org calcolati."
          />
        </>
      )}
    </Modal>
  );
};

export default ImportQuoteModal;
